'use client'

import { type AxiosError } from 'axios'
import useSwr, { mutate, type SWRResponse } from 'swr'
import useSWRInfinite, { type SWRInfiniteResponse } from 'swr/infinite'

import {
  type AccountControllerV2ApiAccountV2TenantLanguageOrdersGetRequest,
  AccountControllerV2ApiFactory,
  type SuccessReadUserOrdersV2,
} from '@redteclab/api/clients/bully'

import { useBullyApi } from '../../base-api-communication'
import { useGlobalConfigContext } from '../../global-config'
import { orderStatusAccessorEnrichMultipleOrdersStatus } from '../orderStatus/orderStatusAccessorEnrichMultipleOrdersStatus'

const SWR_KEY = 'userOrdersGet'

const getSwrKey = (page?: number): string => {
  return page ? `userOrderGet_${page}` : SWR_KEY
}

export const useApiOrdersSwrGetOrders = (options?: {
  itemsPerPage: number
  page?: number
}): SWRResponse<SuccessReadUserOrdersV2, AxiosError> => {
  const config = useGlobalConfigContext()
  const client = useBullyApi(AccountControllerV2ApiFactory)
  const fetcher = async (): Promise<SuccessReadUserOrdersV2> => {
    const response = await client.accountV2TenantLanguageOrdersGet({
      itemsPerPage: options?.itemsPerPage,
      language: config.language,
      page: options?.page,
      tenant: config.tenantAndEnv,
    })

    orderStatusAccessorEnrichMultipleOrdersStatus(response.data)

    return response.data
  }

  return useSwr(getSwrKey(options?.page), fetcher)
}

export const refetchUseApiOrdersSwrGetOrders = async (
  page?: number,
): Promise<SuccessReadUserOrdersV2 | undefined> => {
  return mutate<SuccessReadUserOrdersV2>(getSwrKey(page))
}

export const useApiOrdersSwrGetOrdersInfinite = (
  itemsPerPage: number,
): SWRInfiniteResponse<SuccessReadUserOrdersV2, AxiosError> => {
  const config = useGlobalConfigContext()
  const client = useBullyApi(AccountControllerV2ApiFactory)

  const getKey = (
    pageIndex: number,
  ): AccountControllerV2ApiAccountV2TenantLanguageOrdersGetRequest & {
    key: string
  } => ({
    itemsPerPage,
    key: SWR_KEY,
    language: config.language,
    page: pageIndex,
    tenant: config.tenantAndEnv,
  })

  const fetcher = async (
    key: ReturnType<typeof getKey>,
  ): Promise<SuccessReadUserOrdersV2> => {
    const response = await client.accountV2TenantLanguageOrdersGet({
      itemsPerPage: key.itemsPerPage,
      language: key.language,
      page: key.page,
      tenant: key.tenant,
    })

    orderStatusAccessorEnrichMultipleOrdersStatus(response.data)

    return response.data
  }

  return useSWRInfinite(getKey, fetcher)
}
